import React, {ChangeEventHandler, KeyboardEventHandler, ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {Field} from "./Form";
import {useField} from "formik";

interface LiveSearchProps<T> {
    name: string;
    label?: string;
    placeholder?: string;
    explication?: string;
    results?: T[];
    renderItem(item: T): ReactElement;
    onChange?: ChangeEventHandler;
    onSelect?: (item: T | undefined) => void;
    value?: string;
}

const LiveSearch = <T extends object>({
    name,
    label,
    placeholder,
    explication,
    results = [],
    renderItem,
    value,
    onChange,
    onSelect,
}: LiveSearchProps<T>): ReactElement => {
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const resultContainer = useRef<HTMLDivElement>(null);
    const [showResults, setShowResults] = useState(false);
    const [defaultValue, setDefaultValue] = useState("");

    const [, , helpers] = useField({ 'name': name });

    const handleSelection = (selectedIndex: number) => {
        const selectedItem = results[selectedIndex];
        if (!selectedItem) return resetSearchComplete();
        onSelect && onSelect(selectedItem);
        helpers.setValue(selectedItem);
        resetSearchComplete();
    };

    const resetSearchComplete = useCallback(() => {
        setFocusedIndex(-1);
        setShowResults(false);
    }, []);

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
        const { key } = e;
        let nextIndexCount = 0;

        // move down
        if (key === "ArrowDown")
            nextIndexCount = (focusedIndex + 1) % results.length;

        // move up
        if (key === "ArrowUp")
            nextIndexCount = (focusedIndex + results.length - 1) % results.length;

        // hide search results
        if (key === "Escape") {
            resetSearchComplete();
        }

        // select the current item
        if (key === "Enter") {
            e.preventDefault();
            handleSelection(focusedIndex);
        }

        setFocusedIndex(nextIndexCount);
    };

    type changeHandler = ChangeEventHandler<HTMLInputElement>;
    const handleChange: changeHandler = (e) => {
        setDefaultValue(e.target.value);
        onChange && onChange(e);
        onSelect && onSelect(undefined);
    };

    useEffect(() => {
        if (!resultContainer.current) return;

        resultContainer.current.scrollIntoView({
            block: "center",
        });
    }, [focusedIndex]);

    useEffect(() => {
        if (results.length > 0 && !showResults) setShowResults(true);

        if (results.length <= 0) setShowResults(false);
    }, [results, showResults]);

    useEffect(() => {
        if (value) setDefaultValue(value);
    }, [value]);

    return (
        <div
            tabIndex={1}
            onBlur={resetSearchComplete}
            onKeyDown={handleKeyDown}
            className="relative"
        >
            <Field
                id={name}
                name={name}
                label={label}
                explication={explication}
                value={defaultValue}
                onChange={handleChange}
                autoComplete={"off"}
                placeholder={placeholder}
            />


            {/* Search Results Container */}
            {showResults && (
                <div className="relative w-full p-2 bg-white shadow-lg rounded-bl rounded-br max-h-56 overflow-y-auto">
                    {results.map((item, index) => {
                        return (
                            <div
                                key={index}
                                onMouseDown={() => handleSelection(index)}
                                ref={index === focusedIndex ? resultContainer : null}
                                style={{
                                    backgroundColor:
                                        index === focusedIndex ? "rgba(0,0,0,0.1)" : "",
                                }}
                                className="cursor-pointer hover:bg-black hover:bg-opacity-10 p-2"
                            >
                                {renderItem(item)}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default LiveSearch;